import { useCallback, useEffect, useRef } from "react";

import type { MarkAnchorOptions } from "../utils/scroll-container";

import { handleListRestoreTableKeyDownCapture } from "../utils/scroll-container";

/**
 * Capture-phase keydown on the table scroll container so Enter/Space on a
 * focused row control records the anchor before navigation. Uses a callback
 * ref so the listener attaches when the table mounts (often conditional).
 */
export const useListRestoreTableKeyboardAnchor = (
	listKey: string,
	markAnchor: (
		id: string,
		element?: HTMLElement | null,
		options?: MarkAnchorOptions,
	) => void,
): ((node: HTMLDivElement | null) => void) => {
	const markAnchorRef = useRef(markAnchor);
	const listKeyRef = useRef(listKey);
	const cleanupRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		markAnchorRef.current = markAnchor;
		listKeyRef.current = listKey;
	}, [listKey, markAnchor]);

	useEffect(() => {
		return () => {
			cleanupRef.current?.();
			cleanupRef.current = null;
		};
	}, []);

	return useCallback((container: HTMLDivElement | null) => {
		cleanupRef.current?.();
		cleanupRef.current = null;

		if (!container) {
			return;
		}

		const onKeyDown = (event: KeyboardEvent) => {
			handleListRestoreTableKeyDownCapture(
				event,
				listKeyRef.current,
				markAnchorRef.current,
			);
		};

		container.addEventListener("keydown", onKeyDown, true);
		cleanupRef.current = () => {
			container.removeEventListener("keydown", onKeyDown, true);
		};
	}, []);
};
